// 청구 마감일·거래처 키 (2026-10-23 거래명세서 1단계 — 계약 §5)
// - 거래처 키(partnerKey)의 정본은 이 파일의 partnerKeyOf 하나. 명세서·입금 기록·청구 마감일·누락 알림이 모두 이걸 쓴다.
// - labBilling/{labId} = 기공소 대표 전용 문서 (rules: 대표 본인만). 환자 정보 없음.
// - 날짜는 모두 한국 시간(KST) 기준 'YYYY-MM-DD'로 비교한다.
import { doc, getDoc, setDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

// ── 거래처 키 ────────────────────────────────────────────────────────────────
// 거래처 이름 정규화 — 거래처 화면(PartnerOverview normBiz)·caseView.normClinicKey 와 같은 규칙
//   (공백·괄호 제거, '치과의원/치과병원/치과' 접미 제거, 소문자)
export const normPartnerName = (n) =>
    String(n == null ? '' : n).replace(/\s|\(.*?\)/g, '').replace(/치과의원|치과병원|치과$/g, '').toLowerCase();

// 실제 회원 uid 가 아닌 toUserId 접두 (비회원 치과: 에이전트 수집·이메일 매핑, 의뢰서 이름 키)
const NAME_ID_PREFIX = /^(agent_|email_|order_)/;
// 이름 기반(비회원) 키 접두
export const NAME_KEY_PREFIX = 'name:';

/**
 * 거래처 키 정본.
 *  - 실제 회원 uid → 그 uid
 *  - 비회원(agent_치과명 / email_치과명 / order_치과명) 또는 uid 없음 → 'name:' + 정규화 이름
 *    (같은 치과가 agent_·email_ 두 키로 갈라져 미수가 나뉘는 것을 막는다 — 거래처 화면의 이름 병합 규칙과 같음)
 *  - 이름도 없으면 '' (원장 대상 아님)
 */
export const partnerKeyOf = (toUserId, name) => {
    const id = String(toUserId || '').trim();
    if (id && !NAME_ID_PREFIX.test(id)) return id;
    const nm = normPartnerName(name || id.replace(NAME_ID_PREFIX, ''));
    return nm ? `${NAME_KEY_PREFIX}${nm}` : '';
};

/**
 * 명세서 → 거래처 키. 직접 입력 명세서(toUserId '' 또는 isManual)는 '' (원장 대상 아님, 명세 §4-4).
 */
export const partnerKeyOfStatement = (stmt) => {
    if (!stmt) return '';
    const id = String(stmt.toUserId || '').trim();
    if (!id || stmt.isManual === true) return '';
    return partnerKeyOf(id, stmt.toUserName);
};

/** 회원 키인가 (비회원 = 'name:' 키 또는 빈 값). 전월 미수 기본 꺼짐·경고 판정용 */
export const isMemberPartnerKey = (key) => !!key && !String(key).startsWith(NAME_KEY_PREFIX);

// ── KST 날짜 도우미 ─────────────────────────────────────────────────────────
const KST_MS = 9 * 60 * 60 * 1000;
const pad2 = (n) => String(n).padStart(2, '0');

/** Timestamp·Date·숫자·문자열 → Date (없으면 null) */
export const toDateAny = (v) => {
    if (!v) return null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    if (typeof v.toDate === 'function') return v.toDate();
    if (v.seconds !== undefined) return new Date(v.seconds * 1000 + (v.nanoseconds || 0) / 1e6);
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return kstDayStart(v);
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
};

/** 어떤 시각이든 KST 달력 날짜 'YYYY-MM-DD' (이미 'YYYY-MM-DD' 문자열이면 그대로) */
export const kstYmd = (v) => {
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    const d = toDateAny(v);
    if (!d) return '';
    return new Date(d.getTime() + KST_MS).toISOString().slice(0, 10);
};

/** 'YYYY-MM-DD' → 그 날 KST 00:00 의 Date */
export const kstDayStart = (ymd) => {
    const [y, m, d] = String(ymd).split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d) - KST_MS);
};
/** 'YYYY-MM-DD' → 그 날 KST 23:59:59.999 의 Date */
export const kstDayEnd = (ymd) => new Date(kstDayStart(ymd).getTime() + 24 * 60 * 60 * 1000 - 1);

const lastDayOf = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();   // m: 1~12
const ymdOf = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;
const prevMonth = (y, m) => (m === 1 ? [y - 1, 12] : [y, m - 1]);

/** closingDay 정규화: 'end' | 1~28 정수 | null(안 씀·잘못된 값). 29~31은 28로 상한 */
export const normalizeClosingDay = (closingDay) => {
    if (closingDay === 'end') return 'end';
    const n = Number(closingDay);
    if (!Number.isInteger(n) || n < 1) return null;
    return Math.min(n, 28);
};

// 그 달의 마감 '일'
const closeDateIn = (cd, y, m) => (cd === 'end' ? lastDayOf(y, m) : cd);

/**
 * 가장 최근에 '지난' 마감 기간 (KST). 마감일 당일은 아직 안 지난 것으로 본다(다음날부터 대상 — 명세 §3-3).
 *   예) 25일 마감, 오늘 10/26 → 9/26~10/25 '10월 마감분' / 오늘 10/25 → 8/26~9/25 '9월 마감분'
 *   예) 말일 마감, 오늘 11/1 → 10/1~10/31 '10월 마감분'
 * @returns {{ start: Date, end: Date, label: string, from: 'YYYY-MM-DD', to: 'YYYY-MM-DD', closingDay }} | null(마감일 없음)
 *   start = 시작일 KST 00:00, end = 종료일 KST 23:59:59.999
 */
export const lastClosedPeriod = (closingDay, now = new Date()) => {
    const cd = normalizeClosingDay(closingDay);
    if (!cd) return null;
    const today = kstYmd(now);
    const [ty, tm, td] = today.split('-').map(Number);

    let ey = ty, em = tm;
    if (!(closeDateIn(cd, ty, tm) < td)) [ey, em] = prevMonth(ty, tm);
    const ed = closeDateIn(cd, ey, em);

    let from;
    if (cd === 'end') {
        from = ymdOf(ey, em, 1);
    } else {
        const [py, pm] = prevMonth(ey, em);
        const pd = closeDateIn(cd, py, pm);   // 1~28 이므로 다음날은 항상 같은 달 안 (28 → 29일은 2월이면 다음달 1일)
        const startUtc = new Date(Date.UTC(py, pm - 1, pd + 1));
        from = startUtc.toISOString().slice(0, 10);
    }
    const to = ymdOf(ey, em, ed);
    return { start: kstDayStart(from), end: kstDayEnd(to), label: `${em}월 마감분`, from, to, closingDay: cd };
};

/**
 * 마감 초안을 만들어야 하는가.
 *  - 마감일 없음 → false
 *  - 그 치과의 가장 최근 초안·발행본 기간 종료일이 없거나, 지난 마감 기간 시작일보다 앞 → true
 *    (기간이 겹치는 초안·발행본이 있으면 '이미 있음'으로 본다 — 명세 §3-4)
 * @param lastDraftOrPublishedEnd Date | Timestamp | 'YYYY-MM-DD' | null
 */
export const isClosingDue = (closingDay, lastDraftOrPublishedEnd, now = new Date()) => {
    const p = lastClosedPeriod(closingDay, now);
    if (!p) return false;
    const last = kstYmd(lastDraftOrPublishedEnd);
    if (!last) return true;
    return last < p.from;
};

// ── labBilling 문서 ─────────────────────────────────────────────────────────
/**
 * 기공소 청구 설정. 문서가 없으면 { partners: {} }.
 * 대표 본인만 읽을 수 있다(직원 계정이면 권한 오류가 그대로 throw 된다 — 호출부에서 화면에 알릴 것).
 */
export async function getLabBilling(labId) {
    if (!labId) return { partners: {} };
    const snap = await getDoc(doc(db, 'labBilling', labId));
    if (!snap.exists()) return { partners: {} };
    const data = snap.data() || {};
    return { partners: (data.partners && typeof data.partners === 'object') ? data.partners : {} };
}

/**
 * 거래처 청구 마감일 저장. closingDay = 1~28 정수 | 'end' | null(안 씀 → 그 거래처 항목 삭제)
 */
export async function setClosingDay(labId, partnerKey, partnerName, closingDay) {
    if (!labId) throw new Error('labId 없음');
    if (!partnerKey) throw new Error('partnerKey 없음');
    const ref = doc(db, 'labBilling', labId);
    if (closingDay === null || closingDay === undefined || closingDay === '') {
        await setDoc(ref, { partners: { [partnerKey]: deleteField() }, updatedAt: serverTimestamp() }, { merge: true });
        return;
    }
    const cd = closingDay === 'end' ? 'end' : Number(closingDay);
    if (!(cd === 'end' || (Number.isInteger(cd) && cd >= 1 && cd <= 28))) {
        throw new Error('청구 마감일은 1~28일 또는 말일만 고를 수 있습니다');
    }
    await setDoc(ref, {
        partners: { [partnerKey]: { closingDay: cd, partnerName: String(partnerName || '').slice(0, 60) } },
        updatedAt: serverTimestamp(),
    }, { merge: true });
}
